/**
 * Nutrition program creation wizard.
 *
 * This module keeps the wizard state machine and form orchestration in core-ui while pushing
 * step rendering down to the platform packages.
 */
import { NutritionProgram } from "@corpuscomplete/core/nutrition/program";
import React, { useEffect, useMemo, useState } from "react";
import { createStore, useStore as useZustandStore, type StoreApi } from "zustand";
import { createId, safeParseFloat, zFormFloat, zFormInt } from "@corpuscomplete/core/utils";
import { Unit } from "@corpuscomplete/core/common/unit";
import { TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import {
  BodyMetricEntry,
  type BodyMetricSample,
} from "@corpuscomplete/core/body/body-metric-entry";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import type { AnyFieldMeta } from "@tanstack/react-form";
import { useStore as useFormStore } from "@tanstack/react-form";
import type { UserActor } from "@corpuscomplete/core/actor";
import type { Zero } from "@rocicorp/zero";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { CurrentActor } from "../../../actor";
import { AppForm } from "../../../form";
import { z } from "zod/v4";

export namespace NutritionProgramWizard {
  type DataContext = {
    userId: string;
    heightUnitKey: BodyMetricEntry.HeightKey;
    weightUnitKey: BodyMetricEntry.WeightKey;
    rmrEquation: NutritionProgram.Rmr.Equation;
  };
  const DataContext = React.createContext<DataContext | null>(null);

  export const preload = (zero: Zero, { userId }: UserActor) => {
    void zero.run(queries.bodyCharacteristic.forUser({ userId }));
    void zero.run(queries.bodyMetricEntry.latestSample({ userId, key: "weight" }));
    void zero.run(queries.bodyMetricEntry.latest({ userId, type: "body-composition" }));
  };

  export const DataProvider = ({ children }: React.PropsWithChildren) => {
    const actor = CurrentActor.use();
    const userId = actor.userId;
    return (
      <DataContext.Provider
        value={useMemo(
          () => ({
            userId,
            heightUnitKey: "in",
            weightUnitKey: "lb",
            rmrEquation: "cunningham",
          }),
          [userId],
        )}
      >
        {children}
      </DataContext.Provider>
    );
  };

  const useDataContext = () => {
    const context = React.useContext(DataContext);
    if (!context) {
      throw new Error("NutritionProgramWizard.DataContext not found");
    }
    return context;
  };

  function useBodyCharacteristic(): BodyCharacteristic.Info | undefined {
    const { userId } = useDataContext();
    const [bodyCharacteristic] = useQuery(queries.bodyCharacteristic.forUser({ userId }));
    return bodyCharacteristic;
  }

  function useLatestWeight(): BodyMetricSample.Info | undefined {
    const { userId } = useDataContext();
    const [latestWeight] = useQuery(
      queries.bodyMetricEntry.latestSample({ userId, key: "weight" }),
    );
    return latestWeight;
  }

  function useLatestBodyComposition(): BodyMetricEntry.BodyComposition.Info | null {
    const { userId } = useDataContext();
    const [bodyCompositionEntry] = useQuery(
      queries.bodyMetricEntry.latest({ userId, type: "body-composition" }),
    );
    return BodyMetricEntry.BodyComposition.fromEntry(bodyCompositionEntry);
  }

  function useLoadingState(): { isLoading: boolean } {
    const { userId } = useDataContext();
    const [, bodyCharacteristicDetails] = useQuery(queries.bodyCharacteristic.forUser({ userId }));
    const [, weightDetails] = useQuery(
      queries.bodyMetricEntry.latestSample({ userId, key: "weight" }),
    );
    const [, bodyCompositionDetails] = useQuery(
      queries.bodyMetricEntry.latest({ userId, type: "body-composition" }),
    );
    return {
      isLoading: [bodyCharacteristicDetails, weightDetails, bodyCompositionDetails].some(
        (details) => details.type === "unknown",
      ),
    };
  }

  type WeightGoalType = "maintain" | "lose" | "gain";

  type StepState =
    | {
        isComplete: false;
        step: 0;
      }
    | {
        isComplete: false;
        step: 1;
        newBodyComposition: BodyMetricEntry.BodyComposition.Samples | null;
        newBodyCharacteristic: NutritionProgram.Create["bodyCharacteristic"];
        rmrParameters: NutritionProgram.Rmr.Parameters;
      }
    | {
        isComplete: false;
        step: 2;
        newBodyComposition: BodyMetricEntry.BodyComposition.Samples | null;
        newBodyCharacteristic: NutritionProgram.Create["bodyCharacteristic"];
        weightGoalType: WeightGoalType;
        rmrParameters: NutritionProgram.Rmr.Parameters;
        goalOffset: number;
        goal: NutritionProgram.Create["expenditure"]["goal"];
      }
    | {
        isComplete: false;
        step: 3;
        newBodyComposition: BodyMetricEntry.BodyComposition.Samples | null;
        newBodyCharacteristic: NutritionProgram.Create["bodyCharacteristic"];
        expenditure: NutritionProgram.Create["expenditure"];
      }
    | {
        isComplete: false;
        step: 4;
        newBodyComposition: BodyMetricEntry.BodyComposition.Samples | null;
        newBodyCharacteristic: NutritionProgram.Create["bodyCharacteristic"];
        carbsLeftoverPercentage: number;
        expenditure: NutritionProgram.Create["expenditure"];
      }
    | {
        isComplete: false;
        step: 5;
        newBodyComposition: BodyMetricEntry.BodyComposition.Samples | null;
        newBodyCharacteristic: NutritionProgram.Create["bodyCharacteristic"];
        carbsLeftoverPercentage: number;
        proteinGPerKg: number;
        expenditure: NutritionProgram.Create["expenditure"];
      };

  type Store = {
    stepState: StepState;
    nextState: (stepState: StepState) => void;
    onSubmit: () => void;
  };

  type FormsValues = {
    body: {
      bodyCharacteristic: BodyCharacteristic.Form;
      bodyComposition: {
        weight: string;
        height: string;
        leanBodyMass: string;
      };
    };
    weightGoal: {
      type: WeightGoalType | null;
      targetWeight: string;
      weightChangeRate: string;
    };
    activityLevel: {
      value: ActivityLevel | null;
    };
    proteinIntake: {
      value: ProteinIntake | null;
    };
    dietStyle: {
      value: DietStyle | null;
    };
  };
  type BodyFormDefaults = {
    bodyCharacteristic: BodyCharacteristic.Info | undefined;
    latestBodyComposition: BodyMetricEntry.BodyComposition.Info | null;
    latestWeight: BodyMetricSample.Info | undefined;
    heightUnitKey: BodyMetricEntry.HeightKey;
    weightUnitKey: BodyMetricEntry.WeightKey;
  };

  const defaultFormsValues = {
    body: ({
      bodyCharacteristic,
      latestBodyComposition,
      latestWeight,
      heightUnitKey,
      weightUnitKey,
    }: BodyFormDefaults): FormsValues["body"] => {
      const bodyComposition = {
        weight: "",
        height: "",
        leanBodyMass: "",
      };
      if (latestBodyComposition) {
        bodyComposition.weight = Unit.convert(
          latestBodyComposition.samples.weight,
          weightUnitKey,
        ).quantity.toString();
        if (latestBodyComposition.samples.height) {
          bodyComposition.height = Unit.convert(
            latestBodyComposition.samples.height,
            heightUnitKey,
          ).quantity.toString();
        }
        if (latestBodyComposition.samples.leanBodyMass) {
          bodyComposition.leanBodyMass = Unit.convert(
            latestBodyComposition.samples.leanBodyMass,
            weightUnitKey,
          ).quantity.toString();
        }
      } else if (latestWeight) {
        bodyComposition.weight = Unit.convert(latestWeight, weightUnitKey).quantity.toString();
      }
      return {
        bodyCharacteristic: BodyCharacteristic.formDefault(bodyCharacteristic),
        bodyComposition,
      };
    },
    weightGoal: (): FormsValues["weightGoal"] => ({
      type: null,
      targetWeight: "",
      weightChangeRate: "1",
    }),
    activityLevel: (): FormsValues["activityLevel"] => ({
      value: null,
    }),
    dietStyle: (): FormsValues["dietStyle"] => ({
      value: null,
    }),
    proteinIntake: (): FormsValues["proteinIntake"] => ({
      value: null,
    }),
  };

  const getWeightGoalCurrentWeight = (
    state: Extract<StepState, { step: 1 }>,
    weightUnitKey: BodyMetricEntry.WeightKey,
  ) => {
    return Unit.convert(state.rmrParameters.weight, weightUnitKey).quantity;
  };

  const getWeightGoalValidationMessages = ({
    type,
    targetWeight,
    weightChangeRate,
    currentWeight,
  }: {
    type: WeightGoalType | null;
    targetWeight: number | null;
    weightChangeRate: number | null;
    currentWeight: number | null;
  }) => {
    const errors = {
      targetWeight: null as string | null,
      weightChangeRate: null as string | null,
    };

    if (type !== "lose" && type !== "gain") {
      return errors;
    }

    if (targetWeight === null) {
      errors.targetWeight = "Target weight is required";
    } else if (targetWeight <= 0) {
      errors.targetWeight = "Target weight must be greater than 0";
    } else if (currentWeight !== null) {
      if (type === "lose" && targetWeight >= currentWeight) {
        errors.targetWeight = "Target weight must be below your current weight";
      }
      if (type === "gain" && targetWeight <= currentWeight) {
        errors.targetWeight = "Target weight must be above your current weight";
      }
    }

    if (weightChangeRate === null) {
      errors.weightChangeRate = "Weekly rate is required";
    } else if (weightChangeRate <= 0) {
      errors.weightChangeRate = "Weekly rate must be greater than 0";
    }

    return errors;
  };

  const createWeightGoalFormSchema = (currentWeight: number | null) => {
    return z
      .object({
        type: z.enum(["maintain", "lose", "gain"]),
        targetWeight: z.string(),
        weightChangeRate: z.string(),
      })
      .superRefine((form, ctx) => {
        const errors = getWeightGoalValidationMessages({
          type: form.type,
          targetWeight: safeParseFloat(form.targetWeight),
          weightChangeRate: safeParseFloat(form.weightChangeRate),
          currentWeight,
        });

        if (errors.targetWeight) {
          ctx.addIssue({
            code: "custom",
            path: ["targetWeight"],
            message: errors.targetWeight,
          });
        }

        if (errors.weightChangeRate) {
          ctx.addIssue({
            code: "custom",
            path: ["weightChangeRate"],
            message: errors.weightChangeRate,
          });
        }
      });
  };

  const useForms = (store: StoreApi<Store>) => {
    const dataContext = useDataContext();
    const bodyCharacteristic = useBodyCharacteristic();
    const latestWeight = useLatestWeight();
    const latestBodyComposition = useLatestBodyComposition();

    const bodyFormDefaults: BodyFormDefaults = {
      bodyCharacteristic,
      latestBodyComposition,
      latestWeight,
      heightUnitKey: dataContext.heightUnitKey,
      weightUnitKey: dataContext.weightUnitKey,
    };

    const bodyForm = AppForm.useAppForm({
      defaultValues: defaultFormsValues.body(bodyFormDefaults),
      onSubmit: ({ value }) => {
        const { stepState, nextState } = store.getState();
        if (stepState.step !== 0) {
          return;
        }
        const next = nextStepState.body(
          stepState,
          value,
          bodyForm,
          dataContext.rmrEquation,
          dataContext.heightUnitKey,
          dataContext.weightUnitKey,
        );
        if (next) {
          nextState(next);
        }
      },
    });

    const weightGoalCurrentWeight = useZustandStore(store, (state) => {
      if (state.stepState.isComplete || state.stepState.step !== 1) {
        return null;
      }

      return getWeightGoalCurrentWeight(state.stepState, dataContext.weightUnitKey);
    });

    const weightGoalForm = AppForm.useAppForm({
      defaultValues: defaultFormsValues.weightGoal(),
      validators: {
        onSubmit: createWeightGoalFormSchema(weightGoalCurrentWeight),
      },
      onSubmit: ({ value }) => {
        const { stepState, nextState } = store.getState();
        if (stepState.step !== 1) {
          return;
        }
        const next = nextStepState.weightGoal(
          stepState,
          value,
          dataContext.weightUnitKey,
          weightGoalCurrentWeight,
        );
        if (next) {
          nextState(next);
        }
      },
    });

    const activityLevelForm = AppForm.useAppForm({
      defaultValues: defaultFormsValues.activityLevel(),
      validators: {
        onChange: z.object({
          value: z.enum(ACTIVITY_LEVELS),
        }),
      },
      onSubmit: ({ value }) => {
        const { stepState, nextState } = store.getState();
        if (stepState.step !== 2) {
          return;
        }
        const next = nextStepState.activityLevel(stepState, value);
        if (next) {
          nextState(next);
        }
      },
    });

    const dietStyleForm = AppForm.useAppForm({
      defaultValues: defaultFormsValues.dietStyle(),
      validators: {
        onChange: z.object({
          value: z.enum(DIET_STYLES),
        }),
      },
      onSubmit: ({ value }) => {
        const { stepState, nextState } = store.getState();
        if (stepState.step !== 3) {
          return;
        }
        const next = nextStepState.dietStyle(stepState, value);
        if (next) {
          nextState(next);
        }
      },
    });

    const proteinIntakeForm = AppForm.useAppForm({
      defaultValues: defaultFormsValues.proteinIntake(),
      validators: {
        onChange: z.object({
          value: z.enum(PROTEIN_INTAKES),
        }),
      },
      onSubmit: ({ value }) => {
        const { stepState, nextState } = store.getState();
        if (stepState.step !== 4) {
          return;
        }
        const next = nextStepState.proteinIntake(stepState, value);
        if (next) {
          nextState(next);
        }
      },
    });

    return {
      weightGoalForm,
      bodyForm,
      activityLevelForm,
      dietStyleForm,
      proteinIntakeForm,
      bodyFormDefaults,
    };
  };

  const StoreContext = React.createContext<
    | ({
        store: StoreApi<Store>;
      } & ReturnType<typeof useForms>)
    | null
  >(null);
  export const StoreProvider = ({ children }: React.PropsWithChildren) => {
    const { userId } = useDataContext();
    const z = useZero();
    const [store] = useState(() =>
      createStore<Store>((set, get) => ({
        stepState: {
          isComplete: false,
          step: 0,
        },
        nextState: (stepState) => {
          set({
            stepState,
          });
        },
        onSubmit: () => {
          const { stepState } = get();
          if (stepState.step === 5) {
            const defaultDay = getDefaultDay(stepState);
            const nutrientTargets = getDailyTargets(stepState, defaultDay);
            if (nutrientTargets instanceof Error) {
              return;
            }
            const today = Temporal.Now.plainDateISO();
            z.mutate(
              mutators.nutritionProgram.create({
                id: createId(),
                userId,
                dateRange: {
                  dateStart: today,
                  dateEnd: null,
                },
                days: [
                  {
                    id: createId(),
                    ...defaultDay,
                  },
                ],
                expenditure: stepState.expenditure,
                bodyComposition: stepState.newBodyComposition && {
                  entryId: createId(),
                  samples: stepState.newBodyComposition,
                },
                bodyCharacteristic: stepState.newBodyCharacteristic,
              }),
            );
          }
        },
      })),
    );

    const {
      weightGoalForm,
      bodyForm,
      activityLevelForm,
      dietStyleForm,
      proteinIntakeForm,
      bodyFormDefaults,
    } = useForms(store);

    return (
      <StoreContext.Provider
        value={useMemo(
          () => ({
            store,
            weightGoalForm,
            bodyForm,
            activityLevelForm,
            dietStyleForm,
            proteinIntakeForm,
            bodyFormDefaults,
          }),
          [
            store,
            weightGoalForm,
            bodyForm,
            activityLevelForm,
            dietStyleForm,
            proteinIntakeForm,
            bodyFormDefaults,
          ],
        )}
      >
        {children}
      </StoreContext.Provider>
    );
  };
  const useStoreContext = () => {
    const context = React.useContext(StoreContext);
    if (!context) {
      throw new Error("NutritionProgramWizard.StoreContext not found");
    }
    return context;
  };

  function isFieldDefaultValue(fieldMeta: AnyFieldMeta | undefined): boolean {
    if (!fieldMeta) {
      return true;
    }
    return fieldMeta.isDefaultValue;
  }

  const nextStepState = {
    body: (
      state: Extract<StepState, { step: 0 }>,
      formValues: FormsValues["body"],
      form: ReturnType<typeof useForms>["bodyForm"],
      rmrEquation: NutritionProgram.Rmr.Equation,
      heightUnitKey: BodyMetricEntry.HeightKey,
      weightUnitKey: BodyMetricEntry.WeightKey,
    ): StepState | null => {
      let rmrParameters: NutritionProgram.Rmr.Parameters;
      let newBodyComposition: BodyMetricEntry.BodyComposition.Samples | null = null;
      let newBodyCharacteristic: Omit<BodyCharacteristic.Set, "userId"> | null = null;

      const weight = Unit.inputAsQuantity(formValues.bodyComposition.weight, weightUnitKey);
      const height = Unit.inputAsQuantity(formValues.bodyComposition.height, heightUnitKey);
      const leanBodyMass = Unit.inputAsQuantity(
        formValues.bodyComposition.leanBodyMass,
        weightUnitKey,
      );
      const biologicalSex = formValues.bodyCharacteristic.biologicalSex;
      const dateOfBirth = BodyCharacteristic.dateOfBirthFromForm(
        formValues.bodyCharacteristic.dateOfBirth,
      );
      if (weight === null) {
        return null;
      }

      if (rmrEquation === "cunningham") {
        if (leanBodyMass === null) {
          return null;
        }
        rmrParameters = { type: "cunningham", weight, leanBodyMass };
      } else {
        if (biologicalSex === null || height === null || dateOfBirth === null) {
          return null;
        }
        const age = BodyCharacteristic.dateOfBirthToAge(dateOfBirth).value;
        if (age === undefined) {
          return null;
        }
        rmrParameters = {
          type: rmrEquation,
          biologicalSex,
          age,
          weight,
          height,
        };
      }

      const bodyCharacteristicIsDefault =
        isFieldDefaultValue(form.state.fieldMeta["bodyCharacteristic.biologicalSex"]) &&
        isFieldDefaultValue(form.state.fieldMeta["bodyCharacteristic.dateOfBirth"]);
      if (!bodyCharacteristicIsDefault) {
        newBodyCharacteristic = {
          biologicalSex,
          dateOfBirth,
        };
      }

      const bodyCompositionIsDefault =
        isFieldDefaultValue(form.state.fieldMeta["bodyComposition.weight"]) &&
        isFieldDefaultValue(form.state.fieldMeta["bodyComposition.leanBodyMass"]) &&
        isFieldDefaultValue(form.state.fieldMeta["bodyComposition.height"]);
      if (!bodyCompositionIsDefault) {
        newBodyComposition = {
          weight: {
            id: createId(),
            quantity: weight.quantity,
            unitKey: weight.unitKey,
          },
          leanBodyMass: leanBodyMass
            ? {
                id: createId(),
                quantity: leanBodyMass.quantity,
                unitKey: leanBodyMass.unitKey,
              }
            : undefined,
          height: height
            ? {
                id: createId(),
                quantity: height.quantity,
                unitKey: height.unitKey,
              }
            : undefined,
        };
      }

      return {
        isComplete: false,
        step: 1,
        rmrParameters,
        newBodyComposition,
        newBodyCharacteristic,
      };
    },
    weightGoal: (
      state: Extract<StepState, { step: 1 }>,
      form: FormsValues["weightGoal"],
      weightUnitKey: BodyMetricEntry.WeightKey,
      currentWeight: number | null,
    ): StepState | null => {
      if (form.type === null) {
        return null;
      }

      if (form.type === "maintain") {
        return {
          isComplete: false,
          step: 2,
          newBodyComposition: state.newBodyComposition,
          newBodyCharacteristic: state.newBodyCharacteristic,
          rmrParameters: state.rmrParameters,
          weightGoalType: form.type,
          goalOffset: 0,
          goal: null,
        };
      }

      const targetWeight = safeParseFloat(form.targetWeight);
      const weightChangeRate = safeParseFloat(form.weightChangeRate);
      const errors = getWeightGoalValidationMessages({
        type: form.type,
        targetWeight,
        weightChangeRate,
        currentWeight,
      });
      if (
        errors.targetWeight ||
        errors.weightChangeRate ||
        targetWeight === null ||
        weightChangeRate === null
      ) {
        return null;
      }

      const sign = form.type === "lose" ? -1 : 1;
      const signedRate = sign * Math.abs(weightChangeRate);
      const goalOffset = NutritionProgram.calorieOffsetFromWeightChangeRate({
        quantity: signedRate,
        unitKey: weightUnitKey,
      });

      return {
        isComplete: false,
        step: 2,
        newBodyComposition: state.newBodyComposition,
        newBodyCharacteristic: state.newBodyCharacteristic,
        rmrParameters: state.rmrParameters,
        weightGoalType: form.type,
        goalOffset,
        goal: {
          id: createId(),
          dateTarget: null,
          targets: {
            weight: {
              id: createId(),
              target: { type: "exact", value: targetWeight },
              unitKey: weightUnitKey,
            },
          },
        },
      };
    },
    activityLevel: (
      state: Extract<StepState, { step: 2 }>,
      form: FormsValues["activityLevel"],
    ): StepState | null => {
      const activityFactor = ACTIVITY_LEVEL_OPTIONS.find(
        (option) => option.value === form.value,
      )?.activityFactor;
      if (activityFactor === undefined) {
        return null;
      }
      return {
        isComplete: false,
        step: 3,
        newBodyComposition: state.newBodyComposition,
        newBodyCharacteristic: state.newBodyCharacteristic,
        expenditure: {
          activityFactor,
          calibrationOffset: 0,
          rmrParameters: state.rmrParameters,
          goalOffset: state.goalOffset,
          goal: state.goal,
        },
      };
    },
    dietStyle: (
      state: Extract<StepState, { step: 3 }>,
      form: FormsValues["dietStyle"],
    ): StepState | null => {
      const carbsLeftoverPercentage = DIET_STYLE_OPTIONS.find(
        (option) => option.value === form.value,
      )?.carbsLeftoverPercentage;
      if (carbsLeftoverPercentage === undefined) {
        return null;
      }
      return {
        isComplete: false,
        step: 4,
        newBodyComposition: state.newBodyComposition,
        newBodyCharacteristic: state.newBodyCharacteristic,
        expenditure: state.expenditure,
        carbsLeftoverPercentage,
      };
    },
    proteinIntake: (
      state: Extract<StepState, { step: 4 }>,
      form: FormsValues["proteinIntake"],
    ): StepState | null => {
      const proteinGPerKg = PROTEIN_INTAKE_OPTIONS.find(
        (option) => option.value === form.value,
      )?.gPerKg;
      if (proteinGPerKg === undefined) {
        return null;
      }
      return {
        step: 5,
        isComplete: false,
        newBodyComposition: state.newBodyComposition,
        newBodyCharacteristic: state.newBodyCharacteristic,
        expenditure: state.expenditure,
        carbsLeftoverPercentage: state.carbsLeftoverPercentage,
        proteinGPerKg,
      };
    },
  };

  const getDefaultDay = (stepState: Extract<StepState, { step: 5 }>): NutritionProgram.AutoDay => ({
    type: "auto",
    dateSelector: { type: "default" },
    macroDistribution: {
      protein: {
        type: "gPerKg",
        gPerKg: stepState.proteinGPerKg,
      },
      fat: {
        type: "leftoverPercentage",
        leftoverPercentage: 100 - stepState.carbsLeftoverPercentage,
        minimum: NutritionProgram.MacroDistribution.suggestedMinimums.fat(),
      },
      carbohydrate: {
        type: "leftoverPercentage",
        leftoverPercentage: stepState.carbsLeftoverPercentage,
        minimum: NutritionProgram.MacroDistribution.suggestedMinimums.carbohydrate(),
      },
    },
    expenditureOverrideActivityFactor: null,
    expenditureAdditionalOffset: null,
  });

  const getDailyTargets = (
    stepState: Extract<StepState, { step: 5 }>,
    day = getDefaultDay(stepState),
  ): Nutrient.Targets | Error =>
    NutritionProgram.MacroDistribution.calculateNutrientTargets(
      day.macroDistribution,
      NutritionProgram.totalCaloriesForDay(stepState.expenditure, day),
      Unit.convert(stepState.expenditure.rmrParameters.weight, "kg"),
    );

  type ZodFields<T> = {
    [K in keyof T]: z.ZodType<T[K], T[K]> | undefined;
  };

  const bodyFormFieldSchemas = (
    bodyFormDefaults: BodyFormDefaults,
    rmrEquation: NutritionProgram.Rmr.Equation,
  ) => {
    let isEmpty = true;
    const schemas: {
      bodyComposition: ZodFields<FormsValues["body"]["bodyComposition"]>;
      bodyCharacteristic: ZodFields<FormsValues["body"]["bodyCharacteristic"]>;
    } = {
      bodyComposition: {
        weight: undefined,
        leanBodyMass: undefined,
        height: undefined,
      },
      bodyCharacteristic: {
        dateOfBirth: undefined,
        biologicalSex: undefined,
      },
    };
    const { bodyComposition, bodyCharacteristic } = defaultFormsValues.body(bodyFormDefaults);
    switch (rmrEquation) {
      case "cunningham": {
        if (bodyComposition.weight === "" || bodyComposition.leanBodyMass === "") {
          isEmpty = false;
          schemas.bodyComposition.weight = zFormFloat("validation");
          schemas.bodyComposition.leanBodyMass = zFormFloat("validation");
        }
        return { schemas, isEmpty };
      }
      case "harris-benedict":
      case "mifflin-st-jeor": {
        if (bodyComposition.weight === "" || bodyComposition.height === "") {
          isEmpty = false;
          schemas.bodyComposition.weight = zFormFloat("validation");
          schemas.bodyComposition.height = zFormInt("validation");
        }
        if (bodyCharacteristic.dateOfBirth.year === "") {
          isEmpty = false;
          schemas.bodyCharacteristic.dateOfBirth = BodyCharacteristic.dateOfBirthFormSchema();
        }
        if (bodyCharacteristic.biologicalSex === null) {
          isEmpty = false;
          schemas.bodyCharacteristic.biologicalSex = BodyCharacteristic.biologicalSexSchema;
        }
        return { schemas, isEmpty };
      }
    }
  };

  const useStepTitle = () => {
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    switch (stepState.step) {
      case 0:
        return "Body";
      case 1:
        return "Weight Goal";
      case 2:
        return "Activity Level";
      case 3:
        return "Diet Style";
      case 4:
        return "Protein Intake";
      case 5:
        return "Daily Targets";
    }
  };

  const useStepDescription = () => {
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    switch (stepState.step) {
      case 0:
        return "Enter your body measurements to calculate your metabolic rate";
      case 1:
        return "Choose whether you want to maintain, lose, or gain weight";
      case 2:
        return "Select your typical activity level to estimate daily calorie burn";
      case 3:
        return "Pick a diet style that matches how you prefer to eat";
      case 4:
        return "Choose how much protein you want in your diet";
      case 5:
        return "Review your daily calorie and macro targets before creating";
    }
  };

  const useOnBack = () => {
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    const nextState = useZustandStore(useStoreContext().store, (state) => state.nextState);
    switch (stepState.step) {
      case 5:
        return () =>
          nextState({
            isComplete: false,
            step: 4,
            newBodyComposition: stepState.newBodyComposition,
            newBodyCharacteristic: stepState.newBodyCharacteristic,
            carbsLeftoverPercentage: stepState.carbsLeftoverPercentage,
            expenditure: stepState.expenditure,
          });
      case 4:
        return () =>
          nextState({
            step: 3,
            isComplete: false,
            newBodyComposition: stepState.newBodyComposition,
            newBodyCharacteristic: stepState.newBodyCharacteristic,
            expenditure: stepState.expenditure,
          });
      case 3:
        return () =>
          nextState({
            step: 2,
            isComplete: false,
            newBodyComposition: stepState.newBodyComposition,
            newBodyCharacteristic: stepState.newBodyCharacteristic,
            weightGoalType:
              stepState.expenditure.goal === null
                ? "maintain"
                : stepState.expenditure.goalOffset >= 0
                  ? "gain"
                  : "lose",
            goalOffset: stepState.expenditure.goalOffset,
            goal: stepState.expenditure.goal,
            rmrParameters: stepState.expenditure.rmrParameters,
          });
      case 2:
        return () =>
          nextState({
            step: 1,
            isComplete: false,
            newBodyComposition: stepState.newBodyComposition,
            newBodyCharacteristic: stepState.newBodyCharacteristic,
            rmrParameters: stepState.rmrParameters,
          });
      case 1:
        return () =>
          nextState({
            step: 0,
            isComplete: false,
          });
      case 0:
        return null;
    }
  };

  const useOnNext = () => {
    const { store, bodyForm, weightGoalForm, activityLevelForm, dietStyleForm, proteinIntakeForm } =
      useStoreContext();
    const stepState = useZustandStore(store, (state) => state.stepState);
    switch (stepState.step) {
      case 0:
        return () => {
          void bodyForm.handleSubmit();
        };
      case 1:
        return () => {
          void weightGoalForm.handleSubmit();
        };
      case 2:
        return () => {
          void activityLevelForm.handleSubmit();
        };
      case 3:
        return () => {
          void dietStyleForm.handleSubmit();
        };
      case 4:
        return () => {
          void proteinIntakeForm.handleSubmit();
        };
      case 5:
        return null;
    }
  };

  const useOnSubmit = (): (() => void) => {
    return useZustandStore(useStoreContext().store, (state) => state.onSubmit);
  };

  const useProgressValue = () => {
    const { isLoading } = useLoadingState();
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    if (isLoading) {
      return null;
    }

    let value: number;
    if (stepState.step !== null) {
      value = (stepState.step / 6) * 100;
    } else {
      value = 100;
    }

    return value;
  };

  export const useWizardChrome = () => {
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    const { isLoading } = useLoadingState();
    const title = useStepTitle();
    const description = useStepDescription();
    const onBack = useOnBack();
    const onNext = useOnNext();
    const onSubmit = useOnSubmit();
    const progressValue = useProgressValue();
    const completeStep = useCompleteStep();
    const canSubmit = stepState.step === 5 && !(completeStep?.nutrientTargets instanceof Error);

    return {
      isLoading,
      isComplete: stepState.step === 5,
      step: stepState.step,
      title,
      description,
      onBack,
      onNext,
      onSubmit,
      progressValue,
      canSubmit,
    };
  };

  export const useBodyStep = () => {
    const { heightUnitKey, weightUnitKey, rmrEquation } = useDataContext();
    const { bodyForm: form, bodyFormDefaults } = useStoreContext();
    const { schemas, isEmpty } = useMemo(
      () => bodyFormFieldSchemas(bodyFormDefaults, rmrEquation),
      [bodyFormDefaults, rmrEquation],
    );

    useEffect(() => {
      if (isEmpty) {
        void form.handleSubmit();
      }
    }, [isEmpty, form]);

    return {
      form,
      heightUnitKey,
      weightUnitKey,
      validators: {
        dateOfBirth: schemas.bodyCharacteristic.dateOfBirth,
        biologicalSex: schemas.bodyCharacteristic.biologicalSex,
        height: schemas.bodyComposition.height,
        weight: schemas.bodyComposition.weight,
        leanBodyMass: schemas.bodyComposition.leanBodyMass,
      },
    };
  };

  export const WEIGHT_GOAL_TYPE_VALIDATOR = z.enum(["maintain", "lose", "gain"], {
    message: "Weight goal type is required",
  });

  export const WEIGHT_GOAL_NUMBER_VALIDATOR = zFormFloat("validation");

  const WEIGHT_GOAL_OPTIONS = [
    {
      label: "Maintain",
      value: "maintain",
      description: "We'll calculate calories to maintain your current weight",
    },
    {
      label: "Lose",
      value: "lose",
      description: "We'll create a calorie deficit to support weight loss",
    },
    {
      label: "Gain",
      value: "gain",
      description: "We'll create a calorie surplus to support weight gain",
    },
  ] as const;

  export const useWeightGoalStep = () => {
    const { weightUnitKey } = useDataContext();
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    const form = useStoreContext().weightGoalForm;
    const values = useFormStore(form.store, (state) => state.values);
    const currentWeight =
      !stepState.isComplete && stepState.step === 1
        ? getWeightGoalCurrentWeight(stepState, weightUnitKey)
        : null;

    let preview: {
      calorieOffset: number | null;
      weightDiff: number | null;
      expectedDate: string | null;
      daysToGoal: number | null;
    } | null = null;

    if (values.type === "lose" || values.type === "gain") {
      const targetWeight = safeParseFloat(values.targetWeight);
      const rate = safeParseFloat(values.weightChangeRate);

      const hasRate = rate !== null && rate > 0;
      const hasValidGoal =
        currentWeight !== null &&
        targetWeight !== null &&
        ((values.type === "lose" && targetWeight < currentWeight) ||
          (values.type === "gain" && targetWeight > currentWeight));

      const weightDiff =
        hasValidGoal && targetWeight !== null && currentWeight !== null
          ? Math.abs(currentWeight - targetWeight)
          : null;

      const calorieOffset = hasRate
        ? NutritionProgram.calorieOffsetFromWeightChangeRate({
            quantity: rate,
            unitKey: weightUnitKey,
          })
        : null;

      let expectedDate: string | null = null;
      let daysToGoal: number | null = null;
      if (weightDiff !== null && hasRate && rate !== null) {
        daysToGoal = Math.round((weightDiff / rate) * 7);
        expectedDate = TemporalHelpers.formatDate(
          Temporal.Now.plainDateISO().add({ days: daysToGoal }),
          "monthDayYear",
        );
      }

      preview = {
        calorieOffset,
        weightDiff,
        expectedDate,
        daysToGoal,
      };
    }

    return {
      form,
      options: WEIGHT_GOAL_OPTIONS,
      type: values.type,
      currentWeight,
      weightUnitKey,
      preview,
    };
  };

  export const useActivityLevelStep = () => {
    return {
      form: useStoreContext().activityLevelForm,
      options: ACTIVITY_LEVEL_OPTIONS,
    };
  };

  export const useDietStyleStep = () => {
    return {
      form: useStoreContext().dietStyleForm,
      options: DIET_STYLE_OPTIONS,
    };
  };

  export const useProteinIntakeStep = () => {
    return {
      form: useStoreContext().proteinIntakeForm,
      options: PROTEIN_INTAKE_OPTIONS,
    };
  };

  export const useCompleteStep = () => {
    const stepState = useZustandStore(useStoreContext().store, (state) => state.stepState);
    if (stepState.step !== 5) {
      return null;
    }

    return {
      nutrientTargets: getDailyTargets(stepState),
    };
  };

  const ACTIVITY_LEVEL_OPTIONS = [
    {
      value: "sedentary",
      label: "Sedentary",
      description: "Little or no exercise",
      activityFactor: 1.2,
    },
    {
      value: "lightlyActive",
      label: "Lightly Active",
      description: "Light exercise/sports 1-3 days/week",
      activityFactor: 1.375,
    },
    {
      value: "moderatelyActive",
      label: "Moderately Active",
      description: "Moderate exercise/sports 3-5 days/week",
      activityFactor: 1.55,
    },
    {
      value: "veryActive",
      label: "Very Active",
      description: "Hard exercise/sports 6-7 days a week",
      activityFactor: 1.725,
    },
    {
      value: "extraActive",
      label: "Extra Active",
      description: "Very hard exercise/sports & a physical job",
      activityFactor: 1.9,
    },
    {
      value: "professionalAthlete",
      label: "Professional Athlete",
      description: "Train as a professional athlete",
      activityFactor: 2.3,
    },
  ] as const;
  const ACTIVITY_LEVELS = ACTIVITY_LEVEL_OPTIONS.map((option) => option.value);
  type ActivityLevel = (typeof ACTIVITY_LEVELS)[number];

  const PROTEIN_INTAKE_OPTIONS = [
    {
      value: "low",
      label: "Low",
      description: "We'll set protein at 1.4g per kg of body weight",
      gPerKg: 1.4,
    },
    {
      value: "moderate",
      label: "Moderate",
      description: "We'll set protein at 1.8g per kg of body weight",
      gPerKg: 1.8,
    },
    {
      value: "high",
      label: "High",
      description: "We'll set protein at 2.2g per kg of body weight",
      gPerKg: 2.2,
    },
    {
      value: "extraHigh",
      label: "Extra High",
      description: "We'll set protein at 2.6g per kg of body weight",
      gPerKg: 2.6,
    },
  ] as const;
  const PROTEIN_INTAKES = PROTEIN_INTAKE_OPTIONS.map((option) => option.value);
  type ProteinIntake = (typeof PROTEIN_INTAKES)[number];

  const DIET_STYLE_OPTIONS = [
    {
      value: "balanced",
      label: "Balanced",
      description: "Equal split between carbs and fats after protein needs",
      carbsLeftoverPercentage: 50,
    },
    {
      value: "keto",
      label: "Keto",
      description: "Minimal carbs (5%), high fat for ketosis",
      carbsLeftoverPercentage: 5,
    },
    {
      value: "lowCarb",
      label: "Low-carb",
      description: "Reduced carbs (20%), moderate fat intake",
      carbsLeftoverPercentage: 20,
    },
    {
      value: "lowFat",
      label: "Low-fat",
      description: "Higher carbs (80%), minimal fat intake",
      carbsLeftoverPercentage: 80,
    },
  ] as const;
  const DIET_STYLES = DIET_STYLE_OPTIONS.map((option) => option.value);
  type DietStyle = (typeof DIET_STYLES)[number];
}
